import React from "react";

interface AssetCardSkeletonProps {
  /** Additional className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

/**
 * AssetCardSkeleton
 *
 * A shimmer loading skeleton for AssetCard.
 * Used as placeholder while asset data is loading.
 */
export const AssetCardSkeleton = ({
  className = "",
  style,
}: AssetCardSkeletonProps): JSX.Element => {
  return (
    <>
      {/* Inject keyframes */}
      <style>
        {`
          @keyframes makulaShimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>
      <div
        className={`
          flex items-center gap-lg p-lg rounded-lg
          border border-solid border-primary bg-primary
          ${className}
        `}
        style={style}
      >
        {/* Left Section: Thumbnail + Info */}
        <div className="flex items-center gap-lg flex-1 min-w-0">
          {/* Thumbnail Skeleton */}
          <div
            className="flex-shrink-0 w-4xl h-4xl rounded-lg"
            style={{
              background:
                "linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%)",
              backgroundSize: "200% 100%",
              animation: "makulaShimmer 1.5s ease-in-out infinite",
            }}
          />

          {/* Info Skeleton */}
          <div className="flex flex-col gap-xs flex-1 min-w-0">
            {/* Title */}
            <div
              className="h-lg w-3/4 rounded"
              style={{
                background:
                  "linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%)",
                backgroundSize: "200% 100%",
                animation: "makulaShimmer 1.5s ease-in-out infinite",
              }}
            />
            {/* Subtitle */}
            <div
              className="h-md w-1/2 rounded"
              style={{
                background:
                  "linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%)",
                backgroundSize: "200% 100%",
                animation: "makulaShimmer 1.5s ease-in-out infinite",
                animationDelay: "0.15s",
              }}
            />
          </div>
        </div>

        {/* Right Section: Action buttons skeleton */}
        <div className="flex items-center gap-sm flex-shrink-0">
          <div
            className="w-3xl h-3xl rounded"
            style={{
              background:
                "linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%)",
              backgroundSize: "200% 100%",
              animation: "makulaShimmer 1.5s ease-in-out infinite",
              animationDelay: "0.2s",
            }}
          />
          <div
            className="w-3xl h-3xl rounded"
            style={{
              background:
                "linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%)",
              backgroundSize: "200% 100%",
              animation: "makulaShimmer 1.5s ease-in-out infinite",
              animationDelay: "0.25s",
            }}
          />
        </div>
      </div>
    </>
  );
};
